/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState } from 'react'
import ScrollContainer from 'react-indiana-drag-scroll'
import { Waypoint } from 'react-waypoint'

import { useAppContext } from '~/contexts/app-context'
import { Promotion } from '~/gql/graphql'
import { useResizeObserver } from '~/hooks/useResizeObserver'
import { SegmentProps } from '~/interfaces/types'
import { trackSegment } from '~/utils/tracking'

import styles from './styles.module.css'
import { UspItems } from './usp-items'

const DEBOUNCE_TIME = 1000

interface UspsProps {
  promotionItems: Promotion[]
  hasUspAnimated?: boolean
  setHasUspAnimated?: (arg0: boolean) => void
  isHomepage?: boolean
  isMobileView: boolean
  segmentProps?: SegmentProps
}

const Usps: FC<UspsProps> = ({
  promotionItems,
  hasUspAnimated,
  setHasUspAnimated,
  isHomepage = false,
  isMobileView,
  segmentProps = { product: {}, promotion: {} }
}) => {
  const [canScroll, setCanScroll] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  const shouldStopObserving = isHomepage || hasUspAnimated

  const { device } = useAppContext()

  const onResize = ([{ contentRect, target }]: ResizeObserverEntry[]) => {
    const containerWidth = contentRect.width
    const itemsWidth = target.querySelector("[data-selector='usp-items']")?.clientWidth || 0

    setCanScroll(containerWidth < itemsWidth)
  }

  const { containerRef } = useResizeObserver({ stop: shouldStopObserving, onResize })

  useEffect(() => {
    if (shouldAnimate && !hasUspAnimated && setHasUspAnimated) {
      const animationDebounce = setTimeout(() => {
        setHasUspAnimated(true)
        if (!isHomepage) setIsAnimating(true)
      }, DEBOUNCE_TIME)

      return () => clearTimeout(animationDebounce)
    }
  }, [shouldAnimate, hasUspAnimated])

  const uspsOnClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (device !== 'mobile') return

    event.stopPropagation()
    event.preventDefault()

    trackSegment('Element Clicked', {
      element_name: `Mobile usps - SKU: ${segmentProps.product.sku}`,
      element_text: 'Mobile usps',
      element_type: 'button'
    })
  }

  return (
    <Waypoint
      fireOnRapidScroll={false}
      onEnter={() => {
        if (hasUspAnimated || !canScroll) return null
        return setShouldAnimate(true)
      }}
      onLeave={() => {
        if (hasUspAnimated) return null
        if (shouldAnimate) setShouldAnimate(false)
        return true
      }}
    >
      <div
        className={`usps ${styles.wrapper}`}
        ref={containerRef}
        data-is-animating={isAnimating}
        data-is-mobile-view={isMobileView}
        data-action='click->trigger#identity:stop'
        onClick={uspsOnClick}
        tabIndex={-1}
        role='button'
        aria-pressed='false'
        onKeyUp={() => {}}
      >
        {isMobileView ? (
          <ScrollContainer className={`usps ${styles.drag}`} draggingClassName={styles.dragging}>
            <UspItems promotionItems={promotionItems} />
          </ScrollContainer>
        ) : (
          <UspItems promotionItems={promotionItems} />
        )}
      </div>
    </Waypoint>
  )
}

export default Usps
